/**
 * AdminPricing — PRI-437 P8 (credits era)
 * Admin page for the credit price config + discount codes.
 * Routes: /admin/pricing (tabs: Credit Pricing, Discount Codes)
 */
import { useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import {
  getAdminCreditPriceConfig,
  updateAdminCreditPriceConfig,
  getAdminDiscountCodes,
  createAdminDiscountCode,
  type AdminCreditPriceConfig,
  type DiscountCode,
} from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

// ─── Credit Pricing Tab (PRI-437 P8) ─────────────────────────────────────────

const NUM_FIELDS: Array<{ key: keyof AdminCreditPriceConfig; label: string; hint: string; allowZero?: boolean }> = [
  { key: 'price_per_second_cents', label: 'Price per second (cents)', hint: '1.0 = $0.01/s launch rate' },
  // PRI-552: per-frame pricing knob. 0 = not yet active (placeholder until final price lands).
  { key: 'price_per_frame_cents', label: 'Price per frame (cents)', hint: '0.00015 = final PRI-552 rate (≈0.0045¢/s @ 30fps)', allowZero: true },
  { key: 'signup_grant_frames', label: 'Signup grant (frames)', hint: '108,000 = 1h @ 30fps / 30h @ 1fps (PRI-552)', allowZero: true },
  { key: 'signup_grant_seconds', label: 'Signup grant (seconds)', hint: 'Free seconds at account creation' },
  { key: 'card_grant_seconds', label: 'Card grant (seconds)', hint: 'One-time bonus when a card is added' },
  { key: 'signup_grant_expires_after_days', label: 'Grant expiry (days)', hint: 'Free-class credit lifetime' },
  { key: 'auto_refill_threshold_seconds', label: 'Auto-refill threshold (seconds)', hint: 'Refill triggers at/below this' },
  { key: 'min_purchase_cents', label: 'Minimum purchase (cents)', hint: 'Custom-amount floor' },
]

function CreditPricingTab() {
  const [cfg, setCfg] = useState<AdminCreditPriceConfig | null>(null)
  const [edited, setEdited] = useState<Record<string, string>>({})
  const [presets, setPresets] = useState('')
  const [customEnabled, setCustomEnabled] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getAdminCreditPriceConfig()
      .then(r => {
        setCfg(r.config)
        const initial: Record<string, string> = {}
        for (const f of NUM_FIELDS) initial[f.key as string] = String(r.config[f.key] ?? '')
        setEdited(initial)
        setPresets((r.config.allowed_purchase_cents ?? []).join(', '))
        setCustomEnabled(r.config.custom_purchase_enabled ?? true)
      })
      .catch(() => setError('Failed to load credit price config'))
  }, [])

  const fieldError = (v: string, allowZero = false): string | null => {
    const n = Number(v)
    if (v === '' || !Number.isFinite(n) || (allowZero ? n < 0 : n <= 0)) {
      return allowZero ? 'Must be a non-negative number' : 'Must be a positive number'
    }
    return null
  }
  const presetsError = (() => {
    const parts = presets.split(',').map(p => p.trim()).filter(Boolean)
    if (parts.length === 0) return 'At least one preset required'
    if (parts.some(p => !Number.isInteger(Number(p)) || Number(p) <= 0)) return 'Presets must be positive integers (cents)'
    return null
  })()
  const hasErrors = NUM_FIELDS.some(f => fieldError(edited[f.key as string] ?? '', f.allowZero)) || presetsError !== null

  async function handleSave() {
    if (hasErrors) return
    setSaving(true)
    setError(null)
    try {
      const payload: Record<string, unknown> = {
        allowed_purchase_cents: presets.split(',').map(p => Number(p.trim())).filter(Boolean),
        custom_purchase_enabled: customEnabled,
      }
      for (const f of NUM_FIELDS) payload[f.key as string] = Number(edited[f.key as string])
      await updateAdminCreditPriceConfig(payload)
      setToast('Saved')
      setTimeout(() => setToast(null), 2500)
    } catch {
      setError('Failed to save credit price config')
    } finally {
      setSaving(false)
    }
  }

  if (error && !cfg) return <p className="text-error text-body-sm">{error}</p>
  if (!cfg) return <p className="text-steel text-body-sm">Loading…</p>

  return (
    <div data-testid="credit-pricing-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {NUM_FIELDS.map(f => {
          const err = fieldError(edited[f.key as string] ?? '', f.allowZero)
          return (
            <div key={f.key as string} className="bg-obsidian border border-carbon rounded p-4">
              <label className="text-subhead font-semibold text-steel uppercase tracking-wider block mb-1">{f.label}</label>
              <input
                type="text"
                inputMode="decimal"
                data-testid={`cfg-${f.key as string}`}
                value={edited[f.key as string] ?? ''}
                onChange={e => setEdited(prev => ({ ...prev, [f.key as string]: e.target.value }))}
                className={`w-full bg-void border rounded px-3 py-1.5 text-body-sm text-chalk focus:outline-none focus:border-signal ${err ? 'border-error' : 'border-carbon'}`}
              />
              <p className={`text-subhead mt-1 ${err ? 'text-error' : 'text-steel'}`}>{err ?? f.hint}</p>
            </div>
          )
        })}
        <div className="bg-obsidian border border-carbon rounded p-4">
          <label className="text-subhead font-semibold text-steel uppercase tracking-wider block mb-1">Purchase presets (cents, comma-separated)</label>
          <input
            type="text"
            data-testid="cfg-presets"
            value={presets}
            onChange={e => setPresets(e.target.value)}
            className={`w-full bg-void border rounded px-3 py-1.5 text-body-sm text-chalk focus:outline-none focus:border-signal ${presetsError ? 'border-error' : 'border-carbon'}`}
          />
          <p className={`text-subhead mt-1 ${presetsError ? 'text-error' : 'text-steel'}`}>
            {presetsError ?? '1000, 2500, 5000, 10000 = $10 / $25 / $50 / $100'}
          </p>
        </div>
        <div className="bg-obsidian border border-carbon rounded p-4 flex items-center justify-between">
          <div>
            <label className="text-subhead font-semibold text-steel uppercase tracking-wider block mb-1">Custom amounts</label>
            <p className="text-subhead text-steel">Allow non-preset purchase amounts ≥ minimum</p>
          </div>
          <input
            type="checkbox"
            data-testid="cfg-custom-enabled"
            checked={customEnabled}
            onChange={e => setCustomEnabled(e.target.checked)}
            className="w-5 h-5 accent-signal"
          />
        </div>
      </div>

      {error && <p className="text-error text-body-sm mb-4">{error}</p>}
      {toast && <p className="text-success text-body-sm mb-4" data-testid="save-toast">{toast}</p>}

      <button
        data-testid="save-credit-pricing"
        onClick={() => void handleSave()}
        disabled={saving || hasErrors}
        className="bg-signal hover:bg-signal-hover disabled:opacity-50 text-white font-semibold py-2 px-6 rounded transition-colors duration-fast"
      >
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  )
}

// ─── Discount Codes Tab ───────────────────────────────────────────────────────

// 2026-08-07: free_access codes removed with the legacy Web/Pro/Max/MaxHD
// subscription plans — percent_off is the only discount type.

function DiscountCodesTab() {
  const [codes, setCodes] = useState<DiscountCode[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  // New code form
  const [newCode, setNewCode] = useState('')
  const [percentOff, setPercentOff] = useState('')
  const [durationDays, setDurationDays] = useState('')
  const [maxUses, setMaxUses] = useState('')
  const [creating, setCreating] = useState(false)

  function generateRandomCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
    let result = ''
    for (let i = 0; i < 8; i++) result += chars.charAt(Math.floor(Math.random() * chars.length))
    return result
  }

  useEffect(() => {
    getAdminDiscountCodes()
      .then(r => setCodes(r.codes))
      .catch(() => setError('Failed to load discount codes'))
      .finally(() => setLoading(false))
  }, [])

  async function handleCreate() {
    setCreating(true)
    setError(null)
    try {
      const res = await createAdminDiscountCode({
        code: newCode || undefined,
        discount_type: 'percent_off',
        percent_off: Number(percentOff),
        duration_days: durationDays ? Number(durationDays) : null,
        max_uses: maxUses ? Number(maxUses) : null,
      })
      setCodes(prev => [res.code, ...prev])
      setNewCode('')
      setPercentOff('')
      setDurationDays('')
      setMaxUses('')
      setToast(`Code "${res.code.code}" created`)
      setTimeout(() => setToast(null), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div>
      <h2 className="text-h3 font-bold text-chalk mb-2">Discount Codes</h2>
      <p className="text-body-sm text-steel mb-6">
        Create percent-off codes for credit purchases.
      </p>

      {error && (
        <div className="bg-error/10 border border-error/30 rounded p-3 mb-4 text-error text-body-sm">{error}</div>
      )}
      {toast && (
        <div className="bg-success/10 border border-success/30 rounded p-3 mb-4 text-success text-body-sm">{toast}</div>
      )}

      {/* Create form */}
      <div className="bg-obsidian border border-carbon rounded p-6 mb-8">
        <h3 className="text-callout font-semibold text-chalk mb-4">Create new code</h3>
        <div className="grid tablet:grid-cols-2 gap-4">
          <div>
            <label className="text-subhead text-steel uppercase tracking-wider block mb-1">Code (blank = random)</label>
            <div className="flex gap-2">
              <input
                data-testid="new-code-input"
                type="text"
                placeholder="e.g. SUMMER20"
                value={newCode}
                onChange={e => setNewCode(e.target.value.toUpperCase())}
                className="flex-1 bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk font-mono focus:outline-none focus:border-signal"
              />
              <button
                onClick={() => setNewCode(generateRandomCode())}
                className="bg-carbon hover:bg-carbon/80 text-chalk text-subhead font-medium py-2 px-3 rounded transition-colors duration-fast whitespace-nowrap"
              >
                Generate
              </button>
            </div>
          </div>

          <div>
            <label className="text-subhead text-steel uppercase tracking-wider block mb-1">Percent off</label>
            <input
              type="number"
              min={1}
              max={100}
              placeholder="20"
              value={percentOff}
              onChange={e => setPercentOff(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
            />
          </div>

          <div>
            <label className="text-subhead text-steel uppercase tracking-wider block mb-1">Duration (days, blank = indefinite)</label>
            <input
              type="number"
              min={1}
              placeholder="30"
              value={durationDays}
              onChange={e => setDurationDays(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
            />
          </div>

          <div>
            <label className="text-subhead text-steel uppercase tracking-wider block mb-1">Max uses (blank = unlimited)</label>
            <input
              type="number"
              min={1}
              placeholder="100"
              value={maxUses}
              onChange={e => setMaxUses(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            data-testid="new-code-submit"
            onClick={() => void handleCreate()}
            disabled={creating || !percentOff}
            className="bg-signal hover:bg-signal-hover disabled:opacity-50 text-white font-semibold py-2 px-6 rounded transition-colors duration-fast"
          >
            {creating ? 'Creating…' : 'Create code'}
          </button>
        </div>
      </div>

      {/* Codes list */}
      {loading ? (
        <p className="text-steel text-body-sm">Loading codes…</p>
      ) : codes.length === 0 ? (
        <p className="text-steel text-body-sm">No codes yet.</p>
      ) : (
        <div className="bg-obsidian border border-carbon rounded overflow-hidden">
          <div className="grid grid-cols-5 gap-4 px-5 py-3 bg-carbon/30 border-b border-carbon">
            <div className="text-subhead font-semibold text-steel uppercase tracking-wider">Code</div>
            <div className="text-subhead font-semibold text-steel uppercase tracking-wider">Type</div>
            <div className="text-subhead font-semibold text-steel uppercase tracking-wider">Discount</div>
            <div className="text-subhead font-semibold text-steel uppercase tracking-wider">Uses</div>
            <div className="text-subhead font-semibold text-steel uppercase tracking-wider">Status</div>
          </div>
          {codes.map((code, i) => (
            <div
              key={code.id}
              data-testid={`discount-code-row-${code.code}`}
              className={`grid grid-cols-5 gap-4 px-5 py-3 items-center ${
                i < codes.length - 1 ? 'border-b border-carbon' : ''
              }`}
            >
              <div className="text-body-sm font-mono font-semibold text-chalk">{code.code}</div>
              <div className="text-body-sm text-steel">{code.discount_type === 'percent_off' ? 'Percent off' : 'Free access'}</div>
              <div className="text-body-sm text-steel">
                {code.discount_type === 'percent_off' ? `${code.percent_off}%` : code.free_tier}
              </div>
              <div className="text-body-sm text-steel">
                {code.uses_count}/{code.max_uses ?? '∞'}
              </div>
              <div>
                <span className={`text-subhead font-semibold ${code.active ? 'text-success' : 'text-steel'}`}>
                  {code.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main AdminPricing page ───────────────────────────────────────────────────

type AdminTab = 'pricing' | 'discount-codes'

export default function AdminPricing() {
  const { user } = useUser()
  const [tab, setTab] = useState<AdminTab>('pricing')

  const isAdmin = ADMIN_EMAILS.includes(user?.primaryEmailAddress?.emailAddress ?? '')

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <p className="text-error text-body">Admin access required.</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-void text-chalk">
      <div className="max-w-content mx-auto px-6 py-12">
        <h1 className="text-h1 font-bold text-chalk mb-2">Pricing Admin</h1>
        <p className="text-body-sm text-steel mb-8">Manage the metered credit rate, grants, and discount codes.</p>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-obsidian border border-carbon rounded p-1 w-fit">
          <button
            data-testid="admin-tab-pricing"
            onClick={() => setTab('pricing')}
            className={`px-5 py-2 rounded text-body-sm font-semibold transition-colors duration-fast ${
              tab === 'pricing' ? 'bg-signal text-white' : 'text-steel hover:text-chalk'
            }`}
          >
            Credit Pricing
          </button>
          <button
            data-testid="admin-tab-discount-codes"
            onClick={() => setTab('discount-codes')}
            className={`px-5 py-2 rounded text-body-sm font-semibold transition-colors duration-fast ${
              tab === 'discount-codes' ? 'bg-signal text-white' : 'text-steel hover:text-chalk'
            }`}
          >
            Discount Codes
          </button>
        </div>

        {/* Tab content */}
        {tab === 'pricing' && <CreditPricingTab />}
        {tab === 'discount-codes' && <DiscountCodesTab />}
      </div>
    </div>
  )
}
